import { ArrowDownward, ArrowUpward, DeleteForever, ExpandMore, VisibilityOff } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, TextField, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { TranslateText } from '../../../../../../application/language/TranslateText';
import { useI18n } from '../../../../../../application/language/useI18n';
import { observer } from 'mobx-react';
import React, { ChangeEventHandler, useCallback, useState } from 'react';
import { BlockType, ITheoryBlock, ITheoryBlocks } from '../../../../../../service/unit/interface/IUnitTheory';
import { ITags } from '../../../../../../service/unit/interface/theoryBlocks/ITags';
import { IMainLayoutDomainStore } from '../../../../../layout/main/store/domain/IMainLayoutDomainStore';

import { generateUUID } from '../../../../../../utils/generateUUID';
import { FormTextFieldStandard } from '../../../../form/FormTextFieldStandard';
import { ElementFactory } from '../ElementFactory';
import { NestingButton } from './NestingButton';
import { useAdditionalElementsList } from './useAdditionalElementsList';
import { Colors } from '../../../../color/Colors';

export interface IAdditionalElementsList {
  title: string;
  element: ITheoryBlock<any>;
}

export const ADDITIONAL_ELEMENTS_TYPE_LIST_FOR_SPECIAL_TAGS = [
  BlockType.PARAGRAPH,
  BlockType.LIST,
  BlockType.TABLE,
  BlockType.CODE_EXAMPLE,
  BlockType.SPECIAL_BLOCK,
  BlockType.IMAGE,
  BlockType.VIDEO,
];

export interface ITagsBlock {
  layoutDomain: IMainLayoutDomainStore;
  data: ITheoryBlock<ITags>;
  onDelete: Function;
  onUp: Function;
  onDown: Function;
}
export interface IPickedTag {
  name: string;
  subBlocks: ITheoryBlock<ITheoryBlocks>[];
}
export const TagsBlock = observer(({ data: { data, pseudoId }, onDelete, onUp, onDown, layoutDomain }: ITagsBlock) => {
  const { translate } = useI18n();
  const [pickedtag, setPickedTag] = useState<IPickedTag>(null as any);
  const { additionalElementsList } = useAdditionalElementsList({
    includes: ADDITIONAL_ELEMENTS_TYPE_LIST_FOR_SPECIAL_TAGS,
  });

  const addNewElement = (block) => {
    const newBlock: ITheoryBlock<any> & { Element?: any } = JSON.parse(JSON.stringify(block));
    newBlock.pseudoId = generateUUID();
    newBlock.Element = ElementFactory[newBlock.block];
    data.tagsData.contentBlocks.forEach((tag) => {
      if (tag.name === pickedtag.name) {
        tag.subBlocks.push(newBlock);
      }
    });
  };

  const onDeleteSubBlock = (block, name: string) => {
    data.tagsData.contentBlocks.forEach((tag) => {
      if (tag.name === name) {
        const index = tag.subBlocks.indexOf(block);
        if (index !== -1) {
          tag.subBlocks.splice(index, 1);
        }
      }
    });
  };
  const onUpSubBlock = (index: number, name: string) => {
    data.tagsData.contentBlocks.forEach((tag) => {
      if (tag.name === name) {
        if (index === 0 && tag.subBlocks.length !== 0) {
          const firstEl = tag.subBlocks.shift();
          // @ts-ignore
          tag.subBlocks.push(firstEl);
        } else {
          [tag.subBlocks[index], tag.subBlocks[index - 1]] = [tag.subBlocks[index - 1], tag.subBlocks[index]];
        }
      }
    });
  };
  const onDownSubBlock = (index: number, name: string) => {
    data.tagsData.contentBlocks.forEach((tag) => {
      if (tag.name === name) {
        if (index === tag.subBlocks.length - 1 && tag.subBlocks.length !== 0) {
          const lastEl = tag.subBlocks.pop();
          // @ts-ignore
          tag.subBlocks.unshift(lastEl);
        } else {
          [tag.subBlocks[index], tag.subBlocks[index + 1]] = [tag.subBlocks[index + 1], tag.subBlocks[index]];
        }
      }
    });
  };

  const onChangeTags: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      if (+event.target.value > data.tagsCount) {
        data.tagsCount = +event.target.value;
        data.tagsData.contentBlocks.push({
          name: translate('createUnit.tagCount', { count: data.tagsCount }),
          subBlocks: [],
        });
      } else {
        data.tagsCount = +event.target.value;
        data.tagsData.contentBlocks.pop();
      }
    },
    [data, translate],
  );

  return (
    <Box
      key={pseudoId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        background: Colors.bgSurface,
        padding: '16px 42px ',
        '&:hover .action': { visibility: 'visible' },
      }}
    >
      <Accordion sx={{ background: 'transparent', boxShadow: 'none' }} defaultExpanded={true}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            display: 'flex',
            '& .MuiAccordionSummary-expandIconWrapper': { order: -1 },
            alignItems: 'flex-start',
            '& .MuiAccordionSummary-content': {
              margin: 0,
              width: '100%',
              flex: 1,
            },
            '.Mui-expanded': {
              margin: '0 !important',
            },
            '&.Mui-expanded': {
              minHeight: '32px',
            },
            padding: 0,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', width: '100%' }}>
            <Typography sx={{ marginRight: 'auto' }}>
              <TranslateText i18nKey={'createUnit.tags'} />
            </Typography>
            <Box sx={{ display: 'flex', gap: '8px', visibility: 'hidden' }} className="action">
              <DeleteForever
                fontSize="small"
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  onDelete();
                }}
              />
              <VisibilityOff fontSize="small" sx={{ cursor: 'pointer' }} onClick={() => {}} />
              <ArrowUpward
                fontSize="small"
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  onUp();
                }}
              />
              <ArrowDownward
                fontSize="small"
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  onDown();
                }}
              />
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <TextField
            // @ts-ignore
            onWheel={(e) => e.target.blur()}
            variant="outlined"
            type="number"
            value={data.tagsCount}
            label={translate('createUnit.quantity')}
            InputProps={{ inputProps: { min: 0 } }}
            onChange={onChangeTags}
          />
          <Box sx={{ display: 'flex', gap: '16px', flexDirection: 'column' }}>
            {data.tagsData.contentBlocks.map((field, index) => {
              return (
                <FormTextFieldStandard
                  key={`${field.name}-${index}`}
                  variant="outlined"
                  inputProps={{ maxLength: 30 }}
                  label={translate('createUnit.title')}
                  value={field.name}
                  helperText={translate('validation.notMoreThanSymbols', { number: 30 })}
                  onChange={(newValue) => {
                    data.tagsData.contentBlocks[index].name = newValue;
                  }}
                />
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {data.tagsData.contentBlocks.map((tag, index) => {
              return (
                <Box
                  key={`${tag.name}-${index}`}
                  onClick={() => setPickedTag({ name: tag.name, subBlocks: tag.subBlocks })}
                  sx={{
                    padding: '8px 16px',
                    background: pickedtag !== null && tag.name === pickedtag.name ? Colors.primaryActive : Colors.bgElevated,
                    color: pickedtag !== null && tag.name === pickedtag.name ? Colors.textOnPrimary : Colors.textPrimary,
                    border: `1px solid ${Colors.border}`,
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  {tag.name}
                </Box>
              );
            })}
          </Box>
          {pickedtag !== null && (
            <Box className="tag_content" sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {pickedtag.subBlocks.map((block, index) => {
                  const Element = ElementFactory[block.block];
                  const elementData = { data: block.data, pseudoId: block.pseudoId };
                  return (
                    <Box
                      sx={{
                        width: '100%',
                        borderBottom: `1px solid ${Colors.border}`,
                        '& > div': {
                          background: 'transparent',
                        },
                      }}
                      key={block.pseudoId}
                    >
                      <Element
                        layoutDomain={layoutDomain}
                        data={elementData}
                        onDelete={() => onDeleteSubBlock(block, pickedtag.name)}
                        onUp={() => onUpSubBlock(index, pickedtag.name)}
                        onDown={() => onDownSubBlock(index, pickedtag.name)}
                      />
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                {additionalElementsList.map((button) => {
                  return (
                    <NestingButton
                      key={button.title}
                      title={button.title}
                      action={() => addNewElement(button.element)}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
});
